/**
 * Mapeo de nombres de icono (string guardado en `inv_asset_types.icon`)
 * a componentes de lucide-react, MÁS un catálogo agrupado por familias
 * que consume el IconPicker visual del TipoForm (sub-bloque III.3-icons).
 *
 * Convención: el string se guarda en kebab-case en BBDD (ej: "car",
 * "smartphone", "shopping-bag"). El mapa convierte a PascalCase del
 * componente lucide.
 *
 * Si un tipo trae un icon name que no está en el mapa, `getIcon` devuelve
 * <Box /> como fallback genérico (retrocompatible: los slugs antiguos
 * siguen funcionando aunque ampliemos/recortemos el catálogo).
 */

import {
  // Vehículos
  Car, Truck, Bus, Bike, Plane, Ship, Train,
  // Tecnología
  Laptop, Smartphone, Monitor, Tablet, Headphones, Mouse, Keyboard,
  Camera, Cpu, Code, HardDrive, Router, Tv, Speaker, Watch,
  Printer, Server, Wifi, Radio, Phone, Mic, Webcam, Plug,
  // Mobiliario
  Sofa, Armchair, Lamp,
  // Almacén / packaging
  Package, Boxes, Box, Container, Archive,
  Warehouse, ShoppingCart, ShoppingBag,
  // Documentos / libros
  Book, BookOpen, FileText, Files, Folder, FolderOpen, ClipboardList,
  Newspaper, Notebook, Receipt,
  // Herramientas
  Wrench, Hammer, Briefcase, Drill, Scissors, Ruler, Pen, Pencil,
  // Ropa y accesorios
  Shirt, Glasses, Crown, Gem,
  // Comida y bebida
  Coffee, Apple, Beer, Wine, Cake, Utensils, Pizza, Cookie,
  // Edificios
  Building, Building2, Home, Store, Factory, Hotel, School, Landmark,
  // Personas
  User, Users, UserCircle, Contact, UserCog,
  // Finanzas
  CreditCard, Banknote, Wallet, Coins, DollarSign, Euro,
  // Seguridad
  Key, KeyRound, Lock, Unlock, Shield, ShieldCheck,
  // Comunicación
  Bell, Mail, MessageSquare, Send, Inbox, AtSign,
  // Marcadores / estado
  Tag, Bookmark, Star, Pin, Heart, Flag, Trophy, Award, Sparkles,
  Activity, AlertTriangle, AlertCircle, CheckCircle, Info, HelpCircle,
  // Tiempo
  Calendar, Clock, Timer, Hourglass,
  // Mapa / lugar
  Map, MapPin, Globe, Compass,
  // UI / módulo (no expuestos en el picker)
  LayoutDashboard, History, ChevronRight, Plus,
} from 'lucide-react';


const ICON_MAP = {
  // Vehículos
  car: Car, truck: Truck, bus: Bus, bike: Bike,
  plane: Plane, ship: Ship, train: Train,

  // Tecnología
  laptop: Laptop, smartphone: Smartphone, monitor: Monitor, tablet: Tablet,
  headphones: Headphones, mouse: Mouse, keyboard: Keyboard, camera: Camera,
  cpu: Cpu, code: Code, 'hard-drive': HardDrive, router: Router, tv: Tv,
  speaker: Speaker, watch: Watch, printer: Printer, server: Server,
  wifi: Wifi, radio: Radio, phone: Phone, mic: Mic, webcam: Webcam, plug: Plug,

  // Mobiliario
  sofa: Sofa, armchair: Armchair, lamp: Lamp,

  // Almacén / packaging
  package: Package, boxes: Boxes, box: Box, container: Container,
  archive: Archive, warehouse: Warehouse,
  'shopping-cart': ShoppingCart, 'shopping-bag': ShoppingBag,

  // Documentos / libros
  book: Book, 'book-open': BookOpen, 'file-text': FileText, files: Files,
  folder: Folder, 'folder-open': FolderOpen, 'clipboard-list': ClipboardList,
  newspaper: Newspaper, notebook: Notebook, receipt: Receipt,

  // Herramientas
  wrench: Wrench, hammer: Hammer, briefcase: Briefcase, drill: Drill,
  scissors: Scissors, ruler: Ruler, pen: Pen, pencil: Pencil,

  // Ropa y accesorios
  shirt: Shirt, glasses: Glasses, crown: Crown, gem: Gem,

  // Comida y bebida
  coffee: Coffee, apple: Apple, beer: Beer, wine: Wine,
  cake: Cake, utensils: Utensils, pizza: Pizza, cookie: Cookie,

  // Edificios
  building: Building, 'building-2': Building2, home: Home, store: Store,
  factory: Factory, hotel: Hotel, school: School, landmark: Landmark,

  // Personas
  user: User, users: Users, 'user-circle': UserCircle,
  contact: Contact, 'user-cog': UserCog,

  // Finanzas
  'credit-card': CreditCard, banknote: Banknote, wallet: Wallet,
  coins: Coins, 'dollar-sign': DollarSign, euro: Euro,

  // Seguridad
  key: Key, 'key-round': KeyRound, lock: Lock, unlock: Unlock,
  shield: Shield, 'shield-check': ShieldCheck,

  // Comunicación
  bell: Bell, mail: Mail, 'message-square': MessageSquare,
  send: Send, inbox: Inbox, 'at-sign': AtSign,

  // Marcadores / estado
  tag: Tag, bookmark: Bookmark, star: Star, pin: Pin, heart: Heart,
  flag: Flag, trophy: Trophy, award: Award, sparkles: Sparkles,
  activity: Activity, 'alert-triangle': AlertTriangle,
  'alert-circle': AlertCircle, 'check-circle': CheckCircle, info: Info,
  'help-circle': HelpCircle,

  // Tiempo
  calendar: Calendar, clock: Clock, timer: Timer, hourglass: Hourglass,

  // Mapa / lugar
  map: Map, 'map-pin': MapPin, globe: Globe, compass: Compass,
};


/**
 * Catálogo agrupado por familia para el IconPicker.
 * Solo se incluyen iconos que el usuario puede asignar a un tipo;
 * `ModuleIcons` (Dashboard, Catalog, History, etc.) NO aparecen aquí
 * porque son iconos del propio módulo, no para tipos de artículo.
 *
 * El orden de las categorías define el orden en el picker.
 */
export const ICON_CATALOG = [
  {
    category: 'Vehículos',
    icons: ['car', 'truck', 'bus', 'bike', 'plane', 'ship', 'train'],
  },
  {
    category: 'Tecnología',
    icons: [
      'laptop', 'smartphone', 'monitor', 'tablet', 'headphones', 'mouse',
      'keyboard', 'camera', 'cpu', 'code', 'hard-drive', 'router', 'tv',
      'speaker', 'watch', 'printer', 'server', 'wifi', 'radio',
      'phone', 'mic', 'webcam', 'plug',
    ],
  },
  {
    category: 'Mobiliario',
    icons: ['sofa', 'armchair', 'lamp'],
  },
  {
    category: 'Almacén y packaging',
    icons: [
      'package', 'boxes', 'box', 'container', 'archive', 'warehouse',
      'shopping-cart', 'shopping-bag',
    ],
  },
  {
    category: 'Documentos',
    icons: [
      'book', 'book-open', 'file-text', 'files', 'folder', 'folder-open',
      'clipboard-list', 'newspaper', 'notebook', 'receipt',
    ],
  },
  {
    category: 'Herramientas',
    icons: [
      'wrench', 'hammer', 'briefcase', 'drill', 'scissors', 'ruler',
      'pen', 'pencil',
    ],
  },
  {
    category: 'Ropa y accesorios',
    icons: ['shirt', 'glasses', 'crown', 'gem'],
  },
  {
    category: 'Comida y bebida',
    icons: ['coffee', 'apple', 'beer', 'wine', 'cake', 'utensils', 'pizza', 'cookie'],
  },
  {
    category: 'Edificios',
    icons: [
      'building', 'building-2', 'home', 'store', 'factory', 'hotel',
      'school', 'landmark',
    ],
  },
  {
    category: 'Personas',
    icons: ['user', 'users', 'user-circle', 'contact', 'user-cog'],
  },
  {
    category: 'Finanzas',
    icons: ['credit-card', 'banknote', 'wallet', 'coins', 'dollar-sign', 'euro'],
  },
  {
    category: 'Seguridad',
    icons: ['key', 'key-round', 'lock', 'unlock', 'shield', 'shield-check'],
  },
  {
    category: 'Comunicación',
    icons: ['bell', 'mail', 'message-square', 'send', 'inbox', 'at-sign'],
  },
  {
    category: 'Marcadores y estado',
    icons: [
      'tag', 'bookmark', 'star', 'pin', 'heart', 'flag', 'trophy',
      'award', 'sparkles', 'activity', 'alert-triangle', 'alert-circle',
      'check-circle', 'info', 'help-circle',
    ],
  },
  {
    category: 'Tiempo',
    icons: ['calendar', 'clock', 'timer', 'hourglass'],
  },
  {
    category: 'Mapa y lugar',
    icons: ['map', 'map-pin', 'globe', 'compass'],
  },
];


export function getIcon(name) {
  return ICON_MAP[name] || Box;
}

// Iconos fijos usados por el propio módulo (no como icono de tipo)
export const ModuleIcons = {
  Dashboard: LayoutDashboard,
  Catalog: Boxes,
  History: History,
  Permissions: Lock,
  Chevron: ChevronRight,
  Plus: Plus,
};

// Listado plano de iconos disponibles (retrocompatible: lo consumía el
// <select> nativo del TipoForm anterior). Se conserva por compatibilidad
// y porque puede ser útil para tests.
export const AVAILABLE_ICONS = Object.keys(ICON_MAP);
